import type { Row, Stage } from "./contract"
import type { RowChangedPayload } from "../order-sim/runThrough"
import type { GlanceCell, SectionDef, Stages } from "../order-sim/engine"
import { choseCell, draftTail, NUMBER_WORDS, rupees } from "../order-sim/model"

/**
 * The OIO decisions page's content (handoff 4, Sim-Decisions): the numbered sections, the verdict
 * line, the glance, and what a change re-runs. The page itself is order-sim/Decisions.
 */

export const SECTIONS: (SectionDef & { stage: Stage })[] = [
  { stage: "intake", n: "01", title: "Facts", now: "Reading the documents and recording the facts." },
  { stage: "alignment", n: "02", title: "Allegations", now: "Splitting the allegations, mapping the reply." },
  { stage: "threshold", n: "03", title: "Threshold", now: "Working the time limit, year by year." },
  { stage: "findings", n: "04", title: "Findings", now: "Reading the findings for each allegation." },
  { stage: "quantum", n: "05", title: "Quantum", now: "Pricing the quantum through the calculator." },
]
export const DRAFTING_NOW = "Drafting the sections and checking them."

const calls = (stages: Stages) => (stages.findings?.rows ?? []).filter((r) => r.call)
const totalRow = (stages: Stages) => stages.quantum?.rows.find((r) => r.id === "total")
const sectionOf = (stages: Stages) => stages.threshold?.rows.find((r) => r.id === "section")?.text ?? ""

/** Pleaded under s.74 and read under s.73: s.75(2) applied, and the glance says "from s.74". */
export function fromSection(stages: Stages): string {
  const pleaded = stages.intake?.rows.find((r) => r.id === "sections")?.text ?? ""
  const now = sectionOf(stages)
  return now === "s.73" && /\b74\b/.test(pleaded) ? "s.74" : ""
}

/** "Two of three allegations drop. ₹58,268 is confirmed under s.73 with interest and a ₹10,000 penalty. The draft is written and checked." */
export function verdict(stages: Stages): string | null {
  const said = outcome(stages)
  return said ? `${said} ${draftTail(stages)}` : null
}

/** The verdict without the draft's state: what the order decides, once findings and quantum landed. */
export function outcome(stages: Stages): string | null {
  const found = calls(stages)
  const total = totalRow(stages)
  if (!found.length || !total) return null
  const drops = found.filter((r) => r.call === "drop").length
  const words = NUMBER_WORDS
  const of = found.length
  const lead = drops === of
    ? `All ${of === 1 ? "the allegation" : `${of} allegations`} drop. Nothing is confirmed.`
    : `${drops ? `${words[drops] ?? drops} of ${of} allegations ${drops === 1 ? "drops" : "drop"}.` : `No allegation drops.`} ${rupees(total.amount ?? 0)} is confirmed under ${sectionOf(stages)} with interest${total.penalty ? ` and a ${rupees(total.penalty)} penalty` : ""}.`
  return lead
}

export function glance(stages: Stages, changes: RowChangedPayload[]): GlanceCell[] {
  const found = calls(stages)
  const total = totalRow(stages)
  const confirmed = found.filter((r) => r.call !== "drop")
  const from = fromSection(stages)
  return [
    {
      label: "Allegations", value: `${found.length} · ${confirmed.length} confirmed`,
      sub: [found.filter((r) => r.call === "drop").map((r) => r.id).join(", ") + (found.some((r) => r.call === "drop") ? " drop" : ""),
        confirmed.filter((r) => r.call === "reduce").map((r) => r.id).join(", ") + (confirmed.some((r) => r.call === "reduce") ? " reduced" : "")].filter((x) => x.trim()).join(" · "),
    },
    { label: "Section", value: sectionOf(stages), chip: from ? `from ${from}` : undefined, sub: from ? "intent not established · s.75(2)" : "as pleaded" },
    { label: "Confirmed", value: total ? rupees(total.amount ?? 0) : "—", mono: true, sub: total?.penalty ? `+ interest s.50 · penalty ${rupees(total.penalty)}` : "+ interest s.50" },
    choseCell(SECTIONS, stages, changes),
  ]
}

const SECTIONS_OF: Partial<Record<Stage, string[]>> = {
  intake: ["the preamble", "the background", "the submissions", "the hearing"],
  alignment: ["the background", "the submissions"],
  threshold: ["the threshold section"],
  findings: ["the finding section", "the Order section"],
  quantum: ["the finding section", "the Order section", "the closing"],
}
const DOWN: Partial<Record<Stage, Stage[]>> = {
  intake: ["alignment", "threshold", "findings", "quantum"],
  alignment: ["threshold", "findings", "quantum"],
  threshold: ["findings", "quantum"],
  findings: ["quantum"],
  quantum: [],
}
const SECONDS: Partial<Record<Stage, number>> = { intake: 30, alignment: 12, threshold: 18, findings: 47, quantum: 30 }

/** "Re-runs: 05 Quantum for A1 · the A1 finding section · the Order section · the self-check. about 40 s. 01 to 03 and the other findings stay." */
export function rerunLine(stage: string, row: Row): string {
  const later = DOWN[stage as Stage] ?? []
  const aid = (stage === "findings" || stage === "quantum") && row.id.startsWith("A") ? row.id : ""
  const parts: string[] = []
  for (const s of later) {
    const sec = SECTIONS.find((x) => x.stage === s)
    if (sec) parts.push(`${sec.n} ${sec.title}${aid && s === "quantum" ? ` for ${aid}` : ""}`)
  }
  const sections = new Set<string>()
  for (const s of [stage as Stage, ...later]) for (const x of SECTIONS_OF[s] ?? []) sections.add(aid ? x.replace("the finding section", `the ${aid} finding section`) : x)
  parts.push(...sections, "the self-check")
  const seconds = (stage === "findings" ? 0 : SECONDS[stage as Stage] ?? 0) + later.reduce((n, s) => n + (stage === "findings" && s === "quantum" ? 10 : SECONDS[s] ?? 0), 0) + 6 * sections.size + 8
  const before = SECTIONS.findIndex((x) => x.stage === stage)
  const stays = before > 0 ? `${before === 1 ? "01" : `01 to ${SECTIONS[before - 1].n}`}${aid ? " and the other findings" : ""} stay.` : ""
  return `Re-runs: ${parts.join(" · ")}. about ${Math.max(10, Math.round(seconds / 10) * 10)} s. ${stays}`.trim()
}
